// src/LandingPage.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { useAuth } from './AuthContext';
import HeroSection from './HeroSection';

// ── All 5 compliance services ──────────────────────────────────────────────
const SERVICES = [
  {
    tag: 'Annual Filing',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Form 5472 + 1120',
    penalty: '$25,000 penalty avoided',
    desc: 'Upload your bank statement. AI extracts every qualifying transaction, fills the IRS forms, and faxes directly to the IRS Ogden line — with delivery confirmation.',
    bullets: ['AI bank statement parser', 'Pro forma Form 1120 + Form 5472', 'Faxed to IRS with confirmation', 'In-app signature'],
  },
 
  {
    tag: 'Get Your EIN',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    title: 'EIN Application',
    penalty: 'Required to open a US bank account',
    desc: 'We prepare your SS-4 form and fax it directly to the IRS on your behalf. No phone calls, no waiting on hold. Your EIN arrives by mail to your registered address.',
    bullets: ['SS-4 prepared automatically', 'Faxed to correct IRS unit', 'Covers foreign-owned LLCs', 'No US phone number needed'],
  },
  {
    tag: 'US Tax ID',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.8"/>
      </svg>
    ),
    title: 'ITIN Application',
    penalty: 'Needed to file a US tax return',
    desc: 'We build your complete W-7 package — filled form, cover letter, and filing instructions — and email it to you ready to send. No CPA required for a standard application.',
    bullets: ['W-7 form filled out', 'Cover letter included', 'Step-by-step mailing guide', 'Emailed as a ready-to-send package'],
  },

 {
    tag: 'Free — Included',
    free: true,
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M8.5 14.5l2 2 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Annual Report Tracker',
    penalty: 'Free for every LLC — no plan needed',
    desc: 'Track your Delaware or Wyoming state annual report deadline with a live countdown, calendar, and step-by-step filing checklist — free for everyone, no credits or sign-up required.',
    bullets: ['Live due-date countdown', 'Delaware & Wyoming supported', 'Step-by-step filing checklist', 'Free for every LLC, always'],
  },

];

const FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    title: 'AI Statement Parser',
    desc: 'Upload your bank statement PDF. Our AI identifies every qualifying transaction — contributions, distributions, loans — and maps them to the right IRS lines.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'IRS-Ready PDFs',
    desc: 'Every form filled correctly — right checkboxes, right lines, right headers. Pro forma Form 1120, Form 5472, W-7, SS-4. Generated automatically, every time.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.7 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.61 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Faxed Directly to IRS',
    desc: 'We transmit your filing to the correct IRS fax line the moment you confirm. Delivery confirmation sent to your email — no guessing if it arrived.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Sign In-App',
    desc: 'Draw or upload your signature directly in the browser. Embedded in the PDF exactly where the IRS requires it — no printing, no scanning.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Review Before Sending',
    desc: 'Inspect every transaction, edit details, and regenerate the PDF before anything is sent. You stay in full control of what goes to the IRS.',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
        <path d="M14 14h7v7h-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Full Filing History',
    desc: 'Every filing saved to your account. Access fax confirmations, re-download PDFs, and track every year you\'ve filed — all from the sidebar.',
  },
];

const PLANS = [
  {
    badge: 'Single filing',
    badgeClass: 'lp-plan-badge-free',
    name: 'One-Time',
    price: '$50',
    period: '/ filing',
    desc: 'Pay once per paid filing. Annual Report tracking is always free.',
    notices: '1 filing',
    features: [
      { text: 'Any single paid service (5472+1120, EIN, or ITIN)', on: true },
      { text: 'AI transaction extraction', on: true },
      { text: 'IRS-ready PDF generated', on: true },
      { text: 'Faxed to IRS with confirmation', on: true },
      { text: 'In-app signature', on: true },
      { text: 'Free Annual Report tracker included', on: true },
      { text: 'Filing history', on: false },
      { text: 'Multiple tax years', on: false },
    ],
    btnText: 'Get started — $50',
    btnClass: 'lp-plan-btn-outline',
    featured: false,
  },
  {
    badge: 'Best value',
    badgeClass: 'lp-plan-badge-pop',
    name: 'Lifetime',
    price: '$399',
    period: 'one-time',
    desc: 'Pay once, stay compliant every year forever. All paid services included.',
    notices: 'Unlimited',
    features: [
      { text: 'All paid services: 5472+1120, EIN, ITIN', on: true },
      { text: 'Unlimited filings, forever', on: true },
      { text: 'All tax years covered', on: true },
      { text: 'Full filing history', on: true },
      { text: 'Free Annual Report tracker included', on: true },
      { text: 'Priority support', on: true },
    ],
    btnText: 'Get Lifetime — $299',
    btnClass: 'lp-plan-btn-primary',
    featured: true,
  },
];

const FAQS = [
  {
    q: 'What exactly does Taxsuites handle?',
    a: 'Taxsuites covers three paid US compliance filings for foreign-owned LLCs: annual Form 5472 + pro forma 1120 filing (faxed to the IRS), EIN application via SS-4 fax, and ITIN application via a ready-to-mail W-7 package. It also includes a free State Annual Report tracker for every user, no plan required.',
  },
  {
    q: 'What is Form 5472 and do I really need to file it?',
    a: 'Form 5472 is required for any US LLC that is 100% owned by a non-US person. It reports transactions between you and your LLC. The penalty for missing it is $25,000 per year — even if your LLC had no income.',
  },
  {
    q: 'Is the Annual Report tracker really free?',
    a: 'Yes. The State Annual Report tracker — currently covering Delaware and Wyoming — is completely free for every user, with no plan or credits required. Enter your LLC\'s formation state and month to get an instant countdown, due date, and a step-by-step checklist for filing directly with the state yourself.',
  },
  {
    q: 'How does the IRS receive the Form 5472 and EIN filings?',
    a: 'We fax the completed forms directly to the relevant IRS processing centre — the dedicated fax line specified in the IRS instructions for foreign-owned disregarded entities. You receive a confirmation number for each fax sent.',
  },
  {
    q: 'What bank statement should I upload for Form 5472?',
    a: 'Upload a PDF of your US bank account statement — the account you use to transfer money to and from your LLC. Our AI will identify which transactions involve your LLC and ignore everything else.',
  },
  {
    q: 'Is this the same as hiring a CPA?',
    a: 'Taxsuites automates the mechanical process of preparing and filing these forms for a standard foreign-owned single-member LLC with straightforward transactions. For complex situations — multiple owners, loans with interest, cost-sharing arrangements — consult a licensed tax professional.',
  },
  {
    q: 'When is Form 5472 due?',
    a: 'April 15 for most LLCs, with an extension available to October 15. BOI reports are due within 30 days of LLC formation for new companies, or by the current year deadline for existing ones.',
  },
  {
    q: 'Is my bank statement data stored?',
    a: 'Your statement text is processed in memory to extract transactions and is never stored long-term. The extracted transaction list and your filing details are saved to your account so you can access your history.',
  },
];

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
      <div className="faq-question">
        <span>{q}</span>
        <svg className="faq-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="faq-answer"><p>{a}</p></div>
    </div>
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
      <polyline points="20 6 9 17 4 12" stroke="#2E7D52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function Cross() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2, opacity: 0.28 }}>
      <path d="M18 6L6 18M6 6l12 12" stroke="#7A7264" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, showAuthModal } = useAuth();

  const [servicesRef, servicesIn] = useInView(0.05);
  const [featRef,     featIn]     = useInView(0.1);
  const [hiwRef,      hiwIn]      = useInView(0.08);
  const [pricingRef,  pricingIn]  = useInView(0.08);
  const [ctaRef,      ctaIn]      = useInView(0.1);
  const [faqRef,      faqIn]      = useInView(0.1);

  const handleGetStarted = () => {
    if (!user) { showAuthModal('login'); return; }
    navigate('/generate');
  };

  return (
    <div className="lp-root">

      {/* ── HERO ── */}
      <HeroSection onGetStarted={handleGetStarted} />

      {/* ── SERVICES ── */}
      <section className="lp-section lp-services" ref={servicesRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${servicesIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">Four services. One platform.</div>
            <h2 className="lp-section-title">Every compliance requirement<br /><em>your LLC has.</em></h2>
            <p className="lp-services-sub">Foreign-owned US LLCs face a stack of federal requirements. Taxsuites handles all of them — so you can focus on running your business.</p>
          </div>

          <div className={`lp-services-grid ${servicesIn ? 'visible' : ''}`}>
            {SERVICES.map((s, i) => (
              <div
                className="lp-service-card"
                key={i}
                style={{ animationDelay: `${i * 0.09}s` }}
              >
                <div className="lp-service-top">
                  <div className="lp-service-icon-wrap">
                    <div className="lp-service-icon">{s.icon}</div>
                    <span className="lp-service-tag">{s.tag}</span>
                  </div>
                  <h3 className="lp-service-title">{s.title}</h3>
                  <div className={`lp-service-penalty ${s.free ? 'lp-service-penalty--free' : ''}`}>
                    {s.free ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {s.penalty}
                  </div>
                  <p className="lp-service-desc">{s.desc}</p>
                </div>
                <div className="lp-service-divider" />
                <ul className="lp-service-bullets">
                  {s.bullets.map((b, j) => (
                    <li key={j}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <polyline points="20 6 9 17 4 12" stroke="#2E7D52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {b}
                    </li>
                  ))}
                </ul>
                <button className="lp-service-btn" onClick={handleGetStarted}>
                  {s.free ? 'Track for Free' : `File ${s.title}`}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-section lp-hiw-section" id="how-it-works" ref={hiwRef}>
        <div className="lp-section-inner lp-hiw-inner">
          <div className={`lp-hiw-left ${hiwIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">How it works</div>
            <h2 className="lp-section-title lp-hiw-title">
              From bank statement<br /><em>to IRS confirmation.</em>
            </h2>
            <div className="lp-hiw-steps">
              {[
                { n: '01', title: 'Pick your filing', desc: 'Choose from Form 5472, BOI, EIN, or ITIN. Each has its own guided flow — enter your LLC details and we handle the rest.' },
                { n: '02', title: 'Upload or fill in details', desc: 'For Form 5472, drop in a PDF of your bank statement. Our AI reads it and extracts every qualifying transaction. For other filings, answer a short set of questions.' },
                { n: '03', title: 'Review, sign & submit', desc: 'Inspect the filled forms, add your signature, confirm — and we fax directly to the IRS or FinCEN. You get a delivery confirmation number.' },
              ].map((s, i) => (
                <div key={s.n} className="lp-hiw-step" style={{ animationDelay: hiwIn ? `${i * 0.12}s` : '0s' }}>
                  <div className="lp-hiw-step-num">{s.n}</div>
                  <div className="lp-hiw-step-body">
                    <div className="lp-hiw-step-title">{s.title}</div>
                    <div className="lp-hiw-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`lp-hiw-right ${hiwIn ? 'visible' : ''}`}>
            <div className="lp-filing-mockup">
              <div className="lp-mockup-header">
                <div className="lp-mockup-dot lp-dot-red" />
                <div className="lp-mockup-dot lp-dot-yellow" />
                <div className="lp-mockup-dot lp-dot-green" />
                <span className="lp-mockup-title">Taxsuites · Compliance Dashboard</span>
              </div>
              <div className="lp-mockup-body">
                <div className="lp-mockup-status confirmed">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  All filings up to date — 2024
                </div>
                {[
                  { label: 'Form 5472 + 1120', status: 'Filed', fax: 'FAX-2024-00187' },
                  { label: 'Annual Report', status: 'On track', fax: 'DE · Due Mar 1, 2025' },
                  { label: 'EIN (SS-4)', status: 'Filed', fax: 'FAX-SS4-00012' },
                ].map((r, i) => (
                  <div className="lp-mockup-row" key={i}>
                    <span className="lp-mockup-label">{r.label}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      <span className="lp-mockup-val" style={{ color: '#2E7D52', fontSize: 12 }}>✓ {r.status}</span>
                      <span className="lp-mono" style={{ color: '#A09890', fontSize: 10 }}>{r.fax}</span>
                    </div>
                  </div>
                ))}
                <div className="lp-mockup-divider" />
                <div className="lp-mockup-forms">
                  <div className="lp-mockup-form-chip">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                    $25,000 Form 5472 penalty avoided
                  </div>
                  <div className="lp-mockup-form-chip">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                    Annual Report deadline tracked — free
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PLATFORM FEATURES ── */}
      <section className="lp-section lp-features" ref={featRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${featIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">Built for non-US founders</div>
            <h2 className="lp-section-title">Everything included,<br /><em>nothing to configure.</em></h2>
          </div>
          <div className={`lp-features-grid ${featIn ? 'visible' : ''}`}>
            {FEATURES.map((f, i) => (
              <div className="lp-feature-card" key={i} style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="lp-section lp-pricing" ref={pricingRef}>
        <div className="lp-section-inner">
          <div className={`lp-section-header ${pricingIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">Pricing</div>
            <h2 className="lp-section-title">Simple pricing.<br /><em>No surprises.</em></h2>
            <p className="lp-pricing-sub">One filing or lifetime access — no subscriptions, no annual fees.</p>
          </div>

          <div className={`lp-plans-grid lp-plans-grid-2 ${pricingIn ? 'visible' : ''}`}>
            {PLANS.map((plan, i) => (
              <div
                key={i}
                className={`lp-plan-card ${plan.featured ? 'lp-plan-featured' : ''}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <span className={`lp-plan-badge ${plan.badgeClass}`}>{plan.badge}</span>
                <div className="lp-plan-name">{plan.name}</div>
                <div className="lp-plan-price">
                  {plan.price}
                  <span className="lp-plan-period">{plan.period}</span>
                </div>
                <p className="lp-plan-desc">{plan.desc}</p>
                <div className="lp-plan-pill">
                  <span className="lp-plan-pill-label">Filings included</span>
                  <span className="lp-plan-pill-val">{plan.notices}</span>
                </div>
                <div className="lp-plan-divider" />
                <ul className="lp-plan-feats">
                  {plan.features.map((feat, j) => (
                    <li key={j} className={`lp-plan-feat ${feat.on ? '' : 'lp-plan-feat-off'}`}>
                      {feat.on ? <Check /> : <Cross />}
                      {feat.text}
                    </li>
                  ))}
                </ul>
                <button
                  className={`lp-plan-btn ${plan.btnClass}`}
                  onClick={() => navigate('/pricing-plans')}
                >
                  {plan.btnText}
                </button>
              </div>
            ))}
          </div>

          <div className={`lp-pricing-notes ${pricingIn ? 'visible' : ''}`}>
            <span>✓ No subscription</span>
            <span>✓ 3 paid services covered</span>
            <span>✓ Annual Report tracking always free</span>
            <span>✓ Fax confirmation included</span>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section className="lp-cta-band" ref={ctaRef}>
        <div className={`lp-cta-band-inner ${ctaIn ? 'visible' : ''}`}>
          <div className="lp-cta-band-glow" />
          <h2 className="lp-cta-band-title">
            Your LLC has compliance requirements.<br /><em>Handle all of them today.</em>
          </h2>
          <p className="lp-cta-band-sub">
            Form 5472, EIN, ITIN — plus a free Annual Report tracker. Taxsuites covers every federal and state requirement for foreign-owned US LLCs. No CPA needed for standard filings.
          </p>
          <button className="lp-btn-primary lp-btn-lg lp-btn-light" onClick={handleGetStarted}>
            Get started — from $50
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="lp-cta-chips">
            <span>✓ Form 5472 + 1120</span>
            <span>✓ Annual Report (free)</span>
            <span>✓ EIN Application</span>
            <span>✓ ITIN Package</span>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section lp-faq" ref={faqRef}>
        <div className="lp-section-inner lp-faq-inner">
          <div className={`lp-section-header ${faqIn ? 'visible' : ''}`}>
            <div className="lp-eyebrow">FAQ</div>
            <h2 className="lp-section-title">Common questions</h2>
          </div>
          <div className={`lp-faq-list ${faqIn ? 'visible' : ''}`}>
            {FAQS.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

    </div>
  );
}